import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Select,
  MenuItem,
  CardActions,
  TextField,
} from "@mui/material";
import { LoadingButton } from '@mui/lab';
// hooks
import useResponsive from '../../hooks/useResponsive';
// utils
import axios from '../../utils/axios';


const WORK_TYPE_OPTIONS = [
  { label: "네, 풀타임 정규직 근무 중이에요.", value: "현재 풀타임 정규직 근무중" },
  { label: "아니요, 전업 프리랜서에요.", value: "현재 전업 프리랜서" },
  { label: "아니요, 일을 쉬고 있어요. (커리어 휴식기)", value: "현재 커리어 휴식기" },
];

const ON_SITE_WORK_OPTIONS = [
  { label: "네, 상주 출퇴근 근무가 가능해요.", value: "가능" },
  { label: "아니요, 상주 출퇴근 근무는 어려워요.", value: "불가능" },
];

const CHANGING_JOB_OPTIONS = (smUp) => [
  { label: <>지금 직장에 만족해요. {!smUp && <br />}파트타임으로 일할 곳을 찾아요.</>, value: "파트타임으로 일할 곳을 찾아요." },
  { label: <>정말 핏이 맞는다면 {!smUp && <br />}이직도 고려해볼 수 있어요.</>, value: "핏이 맞는다면 이직도 고려해볼 수 있어요." },
  { label: "6개월 내에 이직할 계획이 있어요.", value: "6개월 내에 이직할 계획이 있어요." },
  { label: "빠르게 풀타임으로도 합류할 수 있어요.", value: "빠르게 풀타임으로도 합류할 수 있어요." },
];

// ----------------------------------------------------------------------

export default function CareerStatusCard({ profile, isApply=false, handleAfterApply }) {
  const smUp = useResponsive('up', 'sm');
  const { enqueueSnackbar } = useSnackbar();

  const [isSubmit, setIsSubmit] = useState(false);
  // values
  const [workType, setWorkType] = useState('');
  const [changingJob, setChangingJob] = useState('');
  const [onSiteWorkAvailable, setOnSiteWorkAvailable] = useState('');
  const [onSiteWorkScheduleAndLocation, setOnSiteWorkScheduleAndLocation] = useState('');

  const updateValues = (newStatus) => {
    setWorkType(newStatus.employment_type || '');
    setChangingJob(newStatus.interest_in_changing_job || '');
    setOnSiteWorkAvailable(newStatus.on_site_work_available || '');
    setOnSiteWorkScheduleAndLocation(newStatus.on_site_work_schedule_and_location || '');
  }

  useEffect(() => {
    if (profile && Object.keys(profile).length > 0) {
      updateValues(profile);
    }
  }, [profile]);

  const handleWorkType = async (e) => {
    setWorkType(e.target.value);
  };

  const handleChangingJob = async (e) => {
    setChangingJob(e.target.value);
  };

  const handleOnSiteWorkAvailable = async (e) => {
    setOnSiteWorkAvailable(e.target.value);
    // "불가능"을 선택하면 일정과 지역 필드를 초기화
    if (e.target.value === '불가능') {
      setOnSiteWorkScheduleAndLocation('');
    }
  };

  const handleOnSiteWorkScheduleAndLocation = async (e) => {
    const value = e.target.value;
    // 255자 제한
    if (value.length <= 255) {
      setOnSiteWorkScheduleAndLocation(value);
    }
  };

  const sendStatus = async () => {
    try {
      setIsSubmit(true);
      const sendResponse = await axios.post('api/crews/profile/employment/', {
        employment_type: workType,
        interest_in_changing_job: changingJob,
        on_site_work_available: onSiteWorkAvailable,
        on_site_work_schedule_and_location: onSiteWorkScheduleAndLocation,
      });
      updateValues(sendResponse.data);
      setIsSubmit(false);
      enqueueSnackbar('커리어 상태가 업데이트 되었습니다.', { variant: 'lightning' });
      if (isApply) {
        handleAfterApply();
      }
    } catch (e) {
      console.error(e);
      setIsSubmit(false);
      if (e.response && e.response.status === 400) {
        enqueueSnackbar(e.response.data.message, { variant: 'warning' });
      }
    }
  }

  return (
    <Card id={"careerInput"} sx={{ border: (theme) => `1px solid ${theme.palette.grey[500_32]}` }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>커리어 상태</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          커리어 상태를 자주 업데이트 하면 딱 맞는 제안을 더 많이, 더 빠르게 받을 수 있어요.
        </Typography>

        <Stack sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            현재 정규직으로 일하고 계신가요?
          </Typography>
          <Select
            fullWidth
            value={workType}
            onChange={handleWorkType}
          >
            {WORK_TYPE_OPTIONS.map((option, index) => (
              <MenuItem key={index} value={option.value}
                sx={{
                  mx: 1,
                  my: 0.5,
                  borderRadius: 0.75,
                  typography: 'body2',
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Stack>

        <Stack sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            정규직 이직에도 관심있으신가요?
          </Typography>
          <Select
            fullWidth
            value={changingJob}
            onChange={handleChangingJob}
          >
            {CHANGING_JOB_OPTIONS(smUp)?.map((option, index) => (
              <MenuItem key={index} value={option.value}
                sx={{
                  mx: 1,
                  my: 0.5,
                  borderRadius: 0.75,
                  typography: 'body2',
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Stack>

        <Stack sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            프리랜서 계약 시 상주 출퇴근 근무도 가능하신가요?
          </Typography>
          <Select
            fullWidth
            value={onSiteWorkAvailable}
            onChange={handleOnSiteWorkAvailable}
          >
            {ON_SITE_WORK_OPTIONS.map((option, index) => (
              <MenuItem key={index} value={option.value}
                sx={{
                  mx: 1,
                  my: 0.5,
                  borderRadius: 0.75,
                  typography: 'body2',
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Stack>

        {onSiteWorkAvailable === '가능' && (
          <Stack sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              상주 출퇴근이 가능한 일정과 지역을 알려주세요.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="예: 월,수,금 가능 / 서울 경기권"
              value={onSiteWorkScheduleAndLocation}
              onChange={handleOnSiteWorkScheduleAndLocation}
              helperText={`${onSiteWorkScheduleAndLocation.length}/255자`}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                },
                '& .MuiFormHelperText-root': {
                  textAlign: 'right',
                },
              }}
            />
          </Stack>
        )}
      </CardContent>
      {!isApply && (
        <CardActions sx={{ px: 3, pb: 3, justifyContent: "flex-end" }}>
        <LoadingButton
          size="large"
          variant="contained"
          fullWidth={!smUp}
          onClick={sendStatus}
          loading={isSubmit}
        >
          커리어 상태 저장하기
        </LoadingButton>
      </CardActions>
      )}
      {isApply && (
        <CardActions
          sx={{
            left: 0, bottom: 0, width: 1, px: 2, pb: 3, justifyContent: "flex-end",
            bgcolor: "background.default",
            ...!smUp && {
              position: "fixed",
            }
          }}
        >
          <LoadingButton
            size="large"
            variant={(workType === '' && changingJob === '' && onSiteWorkAvailable === '' && onSiteWorkScheduleAndLocation === '') ? "outlined" : "contained"}
            fullWidth={!smUp}
            onClick={sendStatus}
            loading={isSubmit}
          >
            {(workType === '' && changingJob === '' && onSiteWorkAvailable === '' && onSiteWorkScheduleAndLocation === '') ? "건너뛰기" : "제출하기"}
          </LoadingButton>
        </CardActions>
      )}
    </Card>
  )
}